import React, {CSSProperties, ReactNode} from 'react'

import {formSpacing} from './styles.js'
import FormRowContext, {isFieldRowControl} from './FormRowContext.js'

export interface FormRowProps {
    /** Horizontal space between the items, formSpacing.columnGap by default. Wrapped lines use formSpacing.rowGap */
    gap?: number
    style?: CSSProperties
    children?: ReactNode
}

// Tells each item whether it is a short control (Button, Toggle, ...) at the top of the row, which then centres
// itself on the first line of fields. Every other item gets false, so controls nested deeper aren't moved
const provideRowContext = (children: ReactNode, topAligned: boolean): ReactNode => {
    return React.Children.map(children, (child) => {
        if (!React.isValidElement(child)) {
            return child
        }

        if (child.type === React.Fragment) {
            return provideRowContext((child.props as {children?: ReactNode}).children, topAligned)
        }

        return (
            <FormRowContext.Provider value={topAligned && isFieldRowControl(child.type)}>
                {child}
            </FormRowContext.Provider>
        )
    })
}

/**
 * Places form fields next to each other with the standard spacing between them.
 * Fields grow (flex: 1) by default, so they share the row equally; give a field a width to fix its size.
 * Items are aligned at the top; shorter controls (buttons, checks, toggles) centre themselves on the fields.
 */
const FormRow = ({gap = formSpacing.columnGap, style, children}: FormRowProps) => {
    const topAligned = !style?.alignItems || style.alignItems === 'flex-start' || style.alignItems === 'start'

    return (
        <div style={{display: 'flex', alignItems: 'flex-start', columnGap: gap, rowGap: formSpacing.rowGap, width: '100%', minWidth: 0, ...style}}>
            {provideRowContext(children, topAligned)}
        </div>
    )
}

export default FormRow
